/**
 * DatePicker — on-brand date input with hierarchical zoom-out across cosmic time.
 *
 * Replaces the native <input type="date"> picker. ELEVEN zoom levels:
 *
 *   days       → pick a day                          (1 month window)
 *   years      → pick a year                         (12 yr   · 10^1)
 *   decades    → pick a decade                       (120 yr  · 10^2)
 *   centuries  → pick a century                      (1.2K yr · 10^3)
 *   millennia  → pick a millennium                   (12K yr  · 10^4)
 *   eras       → pick a 10K-year era                 (120K yr · 10^5)
 *   aeons      → pick a 100K-year aeon               (1.2M yr · 10^6)
 *   ages       → pick a 1M-year age                  (12M yr  · 10^7)
 *   epochs     → pick a 10M-year epoch               (120M yr · 10^8)
 *   eons       → pick a 100M-year eon                (1.2B yr · 10^9)
 *   cosmic     → pick a 1B-year cosmic span (cap)    (12B yr  · 10^10)
 *
 * Each level is exactly 10× the window of the previous. The deepest
 * (cosmic, 12B-year window) reaches the Universe's age scale (~13.8B yr).
 *
 * Days grid is rendered with pure-JS calendar math — Zeller's congruence
 * for day-of-week + Gregorian leap year rules. JS Date is NOT used in
 * the grid, so any year renders correctly: April year 11,999,999 shows
 * its 30 days starting on the correct day of the week.
 *
 * Storage is a separate concern — see lib/dates.ts. JS Date can encode
 * years roughly ±270,000; Postgres timestamp roughly 4713 BC to 294,276
 * AD. If a user picks a date outside the storage range, the form-layer
 * validation surfaces a poetic message before the RPC is called.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { SYMBOLIC_PAST, SYMBOLIC_FUTURE, isSymbolicDate } from '@/lib/dates';

export interface DatePickerProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
}

type ViewMode =
  | 'days'
  | 'years'
  | 'decades'
  | 'centuries'
  | 'millennia'
  | 'eras'
  | 'aeons'
  | 'ages'
  | 'epochs'
  | 'eons'
  | 'cosmic'
  | 'beyond';

const VIEW_ORDER: ViewMode[] = [
  'days', 'years', 'decades', 'centuries', 'millennia',
  'eras', 'aeons', 'ages', 'epochs', 'eons', 'cosmic', 'beyond',
];

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

type ZoomMode = Exclude<ViewMode, 'days' | 'beyond'>;

// Window size = cellSize * 12 cells. Each level is 10× the previous.
const ZOOM_WINDOW: Record<ZoomMode, number> = {
  years:     12,
  decades:   120,
  centuries: 1_200,
  millennia: 12_000,
  eras:      120_000,
  aeons:     1_200_000,
  ages:      12_000_000,
  epochs:    120_000_000,
  eons:      1_200_000_000,
  cosmic:    12_000_000_000,
};

// Whisper of the always-already doctrine surfaces at deep zooms
const POETIC_ZOOMS: ViewMode[] = ['aeons', 'ages', 'epochs', 'eons', 'cosmic', 'beyond'];

// ---------------------------------------------------------------------------
// Pure-JS calendar math (no JS Date dependency)
// ---------------------------------------------------------------------------

/** Negative-safe modulo (JS % can return negative for negative dividends). */
function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** Gregorian leap year — for any year, positive or negative. */
function isLeapYearGregorian(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/** Days in a given month/year (1-indexed month). */
function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYearGregorian(year) ? 29 : 28;
  if (month === 4 || month === 6 || month === 9 || month === 11) return 30;
  return 31;
}

/**
 * Day of week for a Gregorian-calendar date. Uses Zeller's congruence,
 * which is exact for any year (no JS Date involvement).
 *
 * Returns 0=Sunday ... 6=Saturday, matching JS Date.getDay() convention.
 */
function dayOfWeek(year: number, month: number, day: number): number {
  let m = month;
  let y = year;
  if (m < 3) {
    m += 12;
    y -= 1;
  }
  const K = mod(y, 100);
  const J = Math.floor(y / 100);
  // Zeller's: h=0 means Saturday, h=1 means Sunday, ..., h=6 means Friday
  const h = mod(
    day +
    Math.floor((13 * (m + 1)) / 5) +
    K +
    Math.floor(K / 4) +
    Math.floor(J / 4) +
    5 * J,
    7
  );
  // Convert to JS-style (0=Sunday)
  return mod(h + 6, 7);
}

// ---------------------------------------------------------------------------
// Helpers (no JS Date)
// ---------------------------------------------------------------------------

function autoFormat(raw: string): string {
  // Preserve symbolic / non-numeric labels untouched (e.g., "Always-already")
  if (/[a-zA-Z]/.test(raw)) return raw;

  // If user types dashes, respect them — supports years longer than 4 digits
  if (raw.includes('-')) {
    const parts = raw.replace(/[^\d-]/g, '').split('-').filter((p) => p.length > 0);
    if (parts.length === 1) return parts[0].slice(0, 10);
    if (parts.length === 2) return `${parts[0].slice(0, 10)}-${parts[1].slice(0, 2)}`;
    return `${parts[0].slice(0, 10)}-${parts[1].slice(0, 2)}-${parts[2].slice(0, 2)}`;
  }
  // No dashes — auto-insert assuming 4-digit year (the common case)
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

function parseYMD(s: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{1,10})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function formatYMD(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function floorTo(unit: number, year: number): number {
  return Math.floor(year / unit) * unit;
}

/** Adaptive year format — comma-separated under 1B, compact (B suffix) above. */
function fmtYear(y: number): string {
  if (Math.abs(y) >= 1_000_000_000) {
    const v = y / 1_000_000_000;
    return Number.isInteger(v) ? `${v}B` : `${v.toFixed(1)}B`;
  }
  return y.toLocaleString('en-US');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DatePicker({
  value,
  onChange,
  disabled,
  placeholder = 'YYYY-MM-DD',
  id,
  ariaLabel = 'Date input',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(
    isSymbolicDate(value) ? 'beyond' : 'days'
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  }, []);

  const initial = parseYMD(value) ?? today;
  const [viewYear, setViewYear] = useState(initial.year);
  const [viewMonth, setViewMonth] = useState(initial.month);

  useEffect(() => {
    const parsed = parseYMD(value);
    if (parsed) {
      setViewYear(parsed.year);
      setViewMonth(parsed.month);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // When opening, default to days view UNLESS value is symbolic (then beyond)
  useEffect(() => {
    if (!open) {
      setViewMode(isSymbolicDate(value) ? 'beyond' : 'days');
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleTextChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(autoFormat(e.target.value));
  }

  function handleSelectDay(day: number) {
    onChange(formatYMD(viewYear, viewMonth, day));
    setOpen(false);
  }

  function handleClear() {
    onChange('');
    setOpen(false);
  }

  function handleSetToday() {
    onChange(formatYMD(today.year, today.month, today.day));
    setViewYear(today.year);
    setViewMonth(today.month);
    setViewMode('days');
  }

  function zoomOut() {
    setViewMode((m) => {
      const idx = VIEW_ORDER.indexOf(m);
      return idx === -1 || idx === VIEW_ORDER.length - 1 ? m : VIEW_ORDER[idx + 1];
    });
  }

  function shift(direction: 1 | -1) {
    if (viewMode === 'days') {
      let m = viewMonth + direction;
      let y = viewYear;
      if (m > 12) { m = 1; y += 1; }
      if (m < 1)  { m = 12; y -= 1; }
      setViewYear(y);
      setViewMonth(m);
      return;
    }
    if (viewMode === 'beyond') return; // beyond view has no shift
    setViewYear(viewYear + direction * ZOOM_WINDOW[viewMode as ZoomMode]);
  }

  function zoomIn(cellStart: number) {
    const idx = VIEW_ORDER.indexOf(viewMode);
    if (idx <= 0) return;
    setViewYear(cellStart);
    setViewMode(VIEW_ORDER[idx - 1]);
  }

  function handleSelectSymbolic(symbol: string) {
    onChange(symbol);
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={value}
          onChange={handleTextChange}
          disabled={disabled}
          maxLength={16}
          className="flex-1 px-5 py-3 border border-ink/20 bg-paper/50 text-base font-display tracking-wider placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-label={open ? 'Close calendar' : 'Open calendar'}
          className="px-4 border border-l-0 border-ink/20 bg-paper/50 text-ink/55 hover:text-ink hover:bg-paper/80 transition-colors disabled:opacity-50"
        >
          <CalendarIcon className="w-5 h-5" />
        </button>
      </div>

      {open && (
        <div className="absolute z-20 mt-2 w-[300px] bg-paper border border-ink/20 shadow-lg p-4 left-0">
          {/* Header — click to zoom out */}
          <div className="flex items-center justify-between mb-3 gap-1">
            <button
              type="button"
              onClick={() => shift(-1)}
              aria-label="Previous"
              disabled={viewMode === 'beyond'}
              className="flex-shrink-0 p-1.5 text-ink/55 hover:text-ink transition-colors disabled:opacity-20 disabled:cursor-default"
            >
              <Chevron direction="left" />
            </button>
            <button
              type="button"
              onClick={zoomOut}
              disabled={viewMode === 'beyond'}
              aria-label="Zoom out"
              className={`display text-sm md:text-base px-1 transition-colors flex-1 truncate ${
                viewMode === 'beyond'
                  ? 'text-ink/85 cursor-default italic'
                  : 'text-ink/85 hover:text-ink cursor-pointer'
              }`}
            >
              {headerLabel(viewMode, viewYear, viewMonth)}
            </button>
            <button
              type="button"
              onClick={() => shift(1)}
              aria-label="Next"
              disabled={viewMode === 'beyond'}
              className="flex-shrink-0 p-1.5 text-ink/55 hover:text-ink transition-colors disabled:opacity-20 disabled:cursor-default"
            >
              <Chevron direction="right" />
            </button>
          </div>

          {/* Body */}
          {viewMode === 'days' ? (
            <DaysGrid
              viewYear={viewYear}
              viewMonth={viewMonth}
              today={today}
              selected={parseYMD(value)}
              onSelect={handleSelectDay}
            />
          ) : viewMode === 'beyond' ? (
            <BeyondView
              currentValue={value}
              onSelect={handleSelectSymbolic}
            />
          ) : (
            <ZoomGrid
              level={viewMode as ZoomMode}
              viewYear={viewYear}
              today={today}
              selected={parseYMD(value)}
              onSelect={zoomIn}
            />
          )}

          {/* Always-already whisper at deep zooms */}
          {POETIC_ZOOMS.includes(viewMode) && (
            <p className="text-[0.65rem] text-ink/40 italic font-display text-center pt-3 leading-relaxed">
              The Line predates this calendar. Orchistness was always-already.
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink/10 text-xs">
            <button
              type="button"
              onClick={handleClear}
              className="text-ink/45 hover:text-ink/80 italic transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleSetToday}
              className="text-ink/45 hover:text-ink/80 italic transition-colors"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header label
// ---------------------------------------------------------------------------

function headerLabel(mode: ViewMode, year: number, month: number): string {
  if (mode === 'days') return `${MONTH_NAMES[month - 1]} ${fmtYear(year)}`;
  if (mode === 'beyond') return 'Beyond all calendars';
  const win = ZOOM_WINDOW[mode as ZoomMode];
  const start = floorTo(win, year);
  return `${fmtYear(start)} – ${fmtYear(start + win - 1)}`;
}

// ---------------------------------------------------------------------------
// Days grid — pure-JS, works at any year
// ---------------------------------------------------------------------------

interface GridSelection {
  year: number;
  month: number;
  day: number;
}

function DaysGrid({
  viewYear,
  viewMonth,
  today,
  selected,
  onSelect,
}: {
  viewYear: number;
  viewMonth: number;
  today: GridSelection;
  selected: GridSelection | null;
  onSelect: (day: number) => void;
}) {
  const cells = useMemo(() => {
    // Pure-JS calculations — no JS Date involvement
    const startDayOfWeek = dayOfWeek(viewYear, viewMonth, 1);
    const totalDays = daysInMonth(viewYear, viewMonth);
    const list: Array<{ day: number | null; isToday: boolean; isSelected: boolean }> = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      list.push({ day: null, isToday: false, isSelected: false });
    }
    for (let d = 1; d <= totalDays; d++) {
      const isToday =
        viewYear === today.year && viewMonth === today.month && d === today.day;
      const isSelected =
        selected !== null &&
        selected.year === viewYear &&
        selected.month === viewMonth &&
        selected.day === d;
      list.push({ day: d, isToday, isSelected });
    }
    while (list.length % 7 !== 0) {
      list.push({ day: null, isToday: false, isSelected: false });
    }
    return list;
  }, [viewYear, viewMonth, today, selected]);

  return (
    <>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_OF_WEEK.map((d) => (
          <div
            key={d}
            className="text-[0.65rem] tracking-[0.15em] uppercase text-ink/40 text-center py-1"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) =>
          cell.day === null ? (
            <div key={`blank-${i}`} aria-hidden />
          ) : (
            <button
              key={`day-${cell.day}-${i}`}
              type="button"
              onClick={() => onSelect(cell.day!)}
              className={`text-sm font-display py-2 transition-colors ${
                cell.isSelected
                  ? 'bg-accent/20 text-ink ring-1 ring-accent/60'
                  : cell.isToday
                    ? 'text-accent ring-1 ring-ink/15 hover:bg-paper/80'
                    : 'text-ink/75 hover:bg-paper/80 hover:text-ink'
              }`}
            >
              {cell.day}
            </button>
          )
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Zoom grid — unified for years through cosmic
// ---------------------------------------------------------------------------

function ZoomGrid({
  level,
  viewYear,
  today,
  selected,
  onSelect,
}: {
  level: ZoomMode;
  viewYear: number;
  today: GridSelection;
  selected: GridSelection | null;
  onSelect: (cellStart: number) => void;
}) {
  const win = ZOOM_WINDOW[level];
  const cellSize = win / 12;
  const start = floorTo(win, viewYear);

  const cells = Array.from({ length: 12 }).map((_, i) => {
    const cellStart = start + i * cellSize;
    const cellEnd = cellStart + cellSize;
    const isToday = today.year >= cellStart && today.year < cellEnd;
    const isSelected =
      selected !== null && selected.year >= cellStart && selected.year < cellEnd;
    return {
      label: cellLabel(cellStart, cellSize),
      value: cellStart,
      isToday,
      isSelected,
    };
  });

  return (
    <div className="grid grid-cols-3 gap-1.5">
      {cells.map((cell) => (
        <button
          key={cell.label + cell.value}
          type="button"
          onClick={() => onSelect(cell.value)}
          className={`text-sm font-display py-3 px-1 transition-colors ${
            cell.isSelected
              ? 'bg-accent/20 text-ink ring-1 ring-accent/60'
              : cell.isToday
                ? 'text-accent ring-1 ring-ink/15 hover:bg-paper/80'
                : 'text-ink/75 hover:bg-paper/80 hover:text-ink'
          }`}
        >
          <span className="block whitespace-nowrap text-xs md:text-sm">
            {cell.label}
          </span>
        </button>
      ))}
    </div>
  );
}

function cellLabel(cellStart: number, cellSize: number): string {
  if (cellSize === 1) return fmtYear(cellStart);  // years view
  return `${fmtYear(cellStart)}s`;                // ranges
}

// ---------------------------------------------------------------------------
// Beyond view — for Orchists who recognize themselves outside time
// ---------------------------------------------------------------------------

function BeyondView({
  currentValue,
  onSelect,
}: {
  currentValue: string;
  onSelect: (symbol: string) => void;
}) {
  const isPastSelected = currentValue === SYMBOLIC_PAST;
  const isFutureSelected = currentValue === SYMBOLIC_FUTURE;
  return (
    <div className="space-y-3 py-1">
      <p className="text-[0.7rem] text-ink/55 italic font-display text-center leading-relaxed pb-1">
        For those who recognize themselves outside the bureaucracy of time.
      </p>

      <button
        type="button"
        onClick={() => onSelect(SYMBOLIC_PAST)}
        className={`block w-full text-left px-4 py-3 transition-colors ${
          isPastSelected
            ? 'bg-accent/20 ring-1 ring-accent/60'
            : 'border border-ink/15 hover:border-ink/40 hover:bg-paper/80'
        }`}
      >
        <div className="display italic text-base text-ink">{SYMBOLIC_PAST}</div>
        <div className="text-[0.7rem] italic font-display text-ink/55 mt-0.5">
          before time &middot; "I have always been here"
        </div>
      </button>

      <button
        type="button"
        onClick={() => onSelect(SYMBOLIC_FUTURE)}
        className={`block w-full text-left px-4 py-3 transition-colors ${
          isFutureSelected
            ? 'bg-accent/20 ring-1 ring-accent/60'
            : 'border border-ink/15 hover:border-ink/40 hover:bg-paper/80'
        }`}
      >
        <div className="display italic text-base text-ink">{SYMBOLIC_FUTURE}</div>
        <div className="text-[0.7rem] italic font-display text-ink/55 mt-0.5">
          after time &middot; recognition that transcends every horizon
        </div>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline icons
// ---------------------------------------------------------------------------

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
      aria-hidden
    >
      <rect x="3" y="4.5" width="14" height="13" rx="0.5" />
      <line x1="3" y1="8" x2="17" y2="8" />
      <line x1="7" y1="3" x2="7" y2="6" />
      <line x1="13" y1="3" x2="13" y2="6" />
    </svg>
  );
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="w-4 h-4"
      aria-hidden
    >
      {direction === 'left' ? (
        <polyline points="10,3 5,8 10,13" />
      ) : (
        <polyline points="6,3 11,8 6,13" />
      )}
    </svg>
  );
}
